<template>
    <div v-if="hasSources" ref="containerRef" class="relative flex w-full min-w-0 flex-nowrap items-center gap-2 overflow-hidden">
        <span v-if="showLabel" ref="labelRef" class="shrink-0 scaffold-details-small text-valence-grey-600">Based on:</span>

        <template v-if="sortedGroups !== undefined">
            <BasePopover
                v-for="group in visibleGroups"
                :key="group.kind"
                as-child
                align="start"
                :side-offset="6"
                content-class="w-80 max-w-[calc(100vw-2rem)] p-3 shadow-lg"
                @update:open="handlePopoverOpen($event, [group], false)">
                <template #trigger>
                    <button
                        type="button"
                        class="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-card-surface-raised px-3 py-1 scaffold-details-small text-valence-grey-850 transition-colors hover:bg-valence-grey-200 focus-ring"
                        :aria-label="`View ${evidenceChipLabel(group)}: ${group.count} evidence ${group.count === 1 ? 'item' : 'items'}`">
                        <EvidenceSourceIcon :kind="group.kind" />
                        <span>{{ evidenceChipLabel(group) }}</span>
                        <span v-if="group.count > 1" class="text-valence-grey-500">{{ group.count }}</span>
                    </button>
                </template>
                <template #content>
                    <EvidencePopoverContent :groups="[group]" @evidence-link-clicked="handleEvidenceLinkClick" />
                </template>
            </BasePopover>

            <BasePopover
                v-if="overflowGroups.length"
                as-child
                align="start"
                :side-offset="6"
                content-class="w-80 max-w-[calc(100vw-2rem)] p-3 shadow-lg"
                @update:open="handlePopoverOpen($event, overflowGroups, true)">
                <template #trigger>
                    <button
                        type="button"
                        class="inline-flex shrink-0 items-center rounded-full bg-card-surface-raised px-3 py-1 scaffold-details-small text-valence-grey-850 transition-colors hover:bg-valence-grey-200 focus-ring"
                        :aria-label="`View ${overflowEvidenceCount} hidden evidence ${overflowEvidenceCount === 1 ? 'item' : 'items'} from ${overflowGroups.length} source ${overflowGroups.length === 1 ? 'type' : 'types'}`">
                        {{ overflowLabel }}
                    </button>
                </template>
                <template #content>
                    <EvidencePopoverContent :groups="overflowGroups" @evidence-link-clicked="handleEvidenceLinkClick" />
                </template>
            </BasePopover>

            <div ref="measurementRef" class="pointer-events-none invisible absolute left-0 top-0 flex items-center gap-2 whitespace-nowrap" aria-hidden="true">
                <span
                    v-for="group in sortedGroups"
                    :key="group.kind"
                    data-evidence-chip-measure
                    class="inline-flex items-center gap-1.5 rounded-full bg-card-surface-raised px-3 py-1 scaffold-details-small text-valence-grey-850">
                    <EvidenceSourceIcon :kind="group.kind" />
                    <span>{{ evidenceChipLabel(group) }}</span>
                    <span v-if="group.count > 1" class="text-valence-grey-500">{{ group.count }}</span>
                </span>
                <span ref="overflowMeasurementRef" class="inline-flex rounded-full bg-card-surface-raised px-3 py-1 scaffold-details-small text-valence-grey-850">+0</span>
            </div>
        </template>

        <span
            v-for="source in legacySources"
            v-else
            :key="source"
            class="inline-flex items-center rounded-full bg-card-surface-raised px-3 py-1 scaffold-details-small text-valence-grey-850">
            {{ source }}
        </span>
    </div>
</template>

<script setup lang="ts">
import BasePopover from "~vue/components/BasePopover.vue";
import {
    calculateVisibleEvidenceGroupCount,
    evidenceChipLabel,
    evidenceSourceCounts,
    type InsightEvidenceAnalyticsContext,
    type InsightEvidenceGroup,
    type InsightEvidenceItem,
    sortEvidenceGroups,
} from "~vue/Labs/evidence";
import EvidenceSourceIcon from "~vue/Labs/EvidenceSourceIcon.vue";
import EvidencePopoverContent from "~vue/Labs/InsightEvidencePopoverContent.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";

const props = withDefaults(
    defineProps<{
        evidenceGroups?: InsightEvidenceGroup[];
        legacySources?: string[];
        maxVisible?: number;
        analyticsContext?: InsightEvidenceAnalyticsContext;
        showLabel?: boolean;
    }>(),
    {
        legacySources: () => [],
        showLabel: true,
    },
);

const containerRef = ref<HTMLElement>();
const labelRef = ref<HTMLElement>();
const measurementRef = ref<HTMLElement>();
const overflowMeasurementRef = ref<HTMLElement>();
const visibleCount = ref(props.maxVisible ?? props.evidenceGroups?.length ?? 0);
let resizeObserver: ResizeObserver | undefined;

const sortedGroups = computed(() => (props.evidenceGroups === undefined ? undefined : sortEvidenceGroups(props.evidenceGroups)));
const visibleGroups = computed(() => sortedGroups.value?.slice(0, visibleCount.value) ?? []);
const overflowGroups = computed(() => sortedGroups.value?.slice(visibleCount.value) ?? []);
const overflowEvidenceCount = computed(() => overflowGroups.value.reduce((total, group) => total + group.count, 0));
const overflowLabel = computed(() => (visibleGroups.value.length ? `+${overflowEvidenceCount.value}` : `${overflowEvidenceCount.value}`));
const hasSources = computed(() => (sortedGroups.value !== undefined ? sortedGroups.value.length > 0 : props.legacySources.length > 0));

function measureVisibleGroups() {
    const groups = sortedGroups.value ?? [];
    if (props.maxVisible !== undefined) {
        visibleCount.value = Math.min(props.maxVisible, groups.length);
        return;
    }

    const containerWidth = containerRef.value?.clientWidth ?? 0;
    const labelWidth = labelRef.value?.offsetWidth ?? 0;
    const groupWidths = Array.from(measurementRef.value?.querySelectorAll<HTMLElement>("[data-evidence-chip-measure]") ?? []).map((element) => element.offsetWidth);
    const overflowElement = overflowMeasurementRef.value;
    if (!containerWidth || (props.showLabel && !labelWidth) || groupWidths.length !== groups.length || !overflowElement) {
        visibleCount.value = groups.length;
        return;
    }

    const overflowWidths = groups.map((_, index) => {
        const hiddenGroupCount = index + 1;
        const hiddenEvidenceCount = groups.slice(-hiddenGroupCount).reduce((total, group) => total + group.count, 0);
        overflowElement.textContent = hiddenGroupCount === groups.length ? `${hiddenEvidenceCount}` : `+${hiddenEvidenceCount}`;
        return overflowElement.offsetWidth;
    });
    visibleCount.value = calculateVisibleEvidenceGroupCount(containerWidth, labelWidth, groupWidths, overflowWidths);
}

function scheduleMeasurement() {
    void nextTick(measureVisibleGroups);
}

onMounted(() => {
    scheduleMeasurement();
    if (containerRef.value && "ResizeObserver" in globalThis) {
        resizeObserver = new ResizeObserver(scheduleMeasurement);
        resizeObserver.observe(containerRef.value);
    }
});

onBeforeUnmount(() => {
    resizeObserver?.disconnect();
    resizeObserver = undefined;
});

watch(() => props.evidenceGroups, scheduleMeasurement, { deep: true });

function analyticsProperties() {
    if (!props.analyticsContext) {
        return undefined;
    }
    return {
        insight_id: props.analyticsContext.insightId,
        experiment_slug: props.analyticsContext.experimentSlug,
        location: props.analyticsContext.location,
    };
}

function handlePopoverOpen(open: boolean, groups: InsightEvidenceGroup[], isOverflow: boolean) {
    if (!open || !props.analyticsContext) {
        return;
    }

    void logUserInteraction(
        AmplitudeEventNames.MYWORK_INSIGHT_EVIDENCE_VIEWED,
        {
            ...analyticsProperties(),
            evidence_source_counts: evidenceSourceCounts(groups),
            is_overflow: isOverflow,
        },
        props.analyticsContext.coachingSessionUuid ?? undefined,
    );
}

function handleEvidenceLinkClick(group: InsightEvidenceGroup, item: InsightEvidenceItem) {
    if (!props.analyticsContext) {
        return;
    }
    void logUserInteraction(
        AmplitudeEventNames.MYWORK_INSIGHT_EVIDENCE_LINK_CLICKED,
        {
            ...analyticsProperties(),
            evidence_source_counts: { [group.kind]: 1 },
            is_external: Boolean(item.external),
        },
        props.analyticsContext.coachingSessionUuid ?? undefined,
    );
}
</script>
